import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useViewBeacon } from "../lib/useViewBeacon";
import { useNavigate } from "react-router-dom";
import {
  api,
  isInsufficientCredits,
  pageGenerationInFlight,
  type Book,
  type Page as PageData,
  type PageGenerationSourceKind,
} from "../api";
import {
  BookView,
  useIsDesktop,
  type FlipBookHandle,
} from "../components/book";
import { BackIcon, ShareIcon } from "../components/icons";
import { ViewCountBadge } from "../components/ViewCountBadge";
import { EditBookButton } from "../components/book/EditBookButton";
import {
  bookIsShareable,
  ShareDialog,
  shareUrlFor,
  useShare,
} from "../components/share";
import { ShareToast } from "./Home";

export default function Reader({
  book,
  pages,
  ownerControls = false,
  onRefresh,
  trackShortId,
  backTo = "/",
}: {
  book: Book;
  pages: PageData[];
  ownerControls?: boolean;
  onRefresh?: () => Promise<void> | void;
  /** Public reader only: short id to fire the view beacon against. */
  trackShortId?: string;
  /** Where the top-bar back button goes. Owner view passes "/app"; the
   *  public share view keeps the default "/" (the landing page). */
  backTo?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    status: shareStatus,
    dialog: shareDialog,
    openDialog,
    closeDialog: closeShare,
    errorMessage: shareError,
    imageState,
    prepareImage,
    deliverImage,
    openImageInNewTab,
    resetImage,
    shareLinkDirect,
  } = useShare();
  const onShareClose = () => {
    resetImage();
    closeShare();
  };
  const [fixTarget, setFixTarget] = useState<PageData | null>(null);
  const [fixSourceKind, setFixSourceKind] = useState<PageGenerationSourceKind>("auto");
  const [fixInstruction, setFixInstruction] = useState("");
  const [fixBusy, setFixBusy] = useState(false);
  const [fixError, setFixError] = useState<string | null>(null);

  const openFixPage = (page: PageData) => {
    if (pageGenerationInFlight(page.latestGeneration)) return;
    setFixTarget(page);
    setFixSourceKind("auto");
    setFixInstruction("");
    setFixError(null);
  };

  const closeFixPage = () => {
    if (fixBusy) return;
    setFixTarget(null);
    setFixError(null);
  };

  const submitFixPage = async () => {
    if (!fixTarget || fixBusy) return;
    setFixBusy(true);
    setFixError(null);
    try {
      await api.regeneratePage(book.id, fixTarget.id, {
        sourceKind: fixSourceKind,
        instruction: fixInstruction.trim() || null,
      });
      setFixTarget(null);
      await Promise.resolve(onRefresh?.()).catch(() => undefined);
    } catch (e) {
      setFixError(isInsufficientCredits(e) ? e.message : String(e));
      await Promise.resolve(onRefresh?.()).catch(() => undefined);
    } finally {
      setFixBusy(false);
    }
  };

  // Ensure any held object URL is revoked if Reader unmounts (route
  // change, tab close inside SPA) without the user explicitly closing
  // the dialog first. `resetImage` is a fresh closure on every
  // useShare() render, so we hold the latest version in a ref and
  // run only on unmount — otherwise [resetImage] deps would re-fire
  // the cleanup every render and trigger an infinite setState loop.
  const resetImageRef = useRef(resetImage);
  resetImageRef.current = resetImage;
  useEffect(() => {
    return () => {
      resetImageRef.current();
    };
  }, []);

  const shareable = bookIsShareable(book);
  const isDesktop = useIsDesktop();

  const flipBookRef = useRef<FlipBookHandle>(null);
  const { noteInteraction } = useViewBeacon(trackShortId);

  // The reader fits one page per screen on mobile, so document-level
  // scrolling is never wanted here — it only shows up as iOS rubber-band
  // or a stray pixel of overflow nudging the layout. Lock body scroll
  // while this route is mounted (mobile only; desktop already constrains
  // .tp-app to the viewport via its own media query).
  useEffect(() => {
    if (isDesktop) return;
    const body = document.body;
    const html = document.documentElement;
    const prevBodyOverflow = body.style.overflow;
    const prevHtmlOverflow = html.style.overflow;
    const prevOverscroll = body.style.overscrollBehavior;
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    return () => {
      body.style.overflow = prevBodyOverflow;
      html.style.overflow = prevHtmlOverflow;
      body.style.overscrollBehavior = prevOverscroll;
    };
  }, [isDesktop]);

  // iOS Safari interprets a touch that starts within ~20–30 px of the left
  // viewport edge as the system back-navigation gesture, which yanks the
  // user out of the book mid-swipe. The reader is the only route where a
  // horizontal swipe is a primary interaction, so we intercept touchstart
  // at the window level for this page only and preventDefault when it
  // lands inside the edge zone — but skip touches inside the topbar so the
  // back-button (which sits in that zone) still works.
  useEffect(() => {
    const EDGE_PX = 30;
    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!e.cancelable || e.touches.length !== 1 || !touch) return;
      const x = touch.clientX;
      const w = window.innerWidth;
      if (x >= EDGE_PX && x <= w - EDGE_PX) return;
      const target = e.target;
      if (target instanceof Element && target.closest(".tp-topbar")) return;
      e.preventDefault();
    };
    window.addEventListener("touchstart", onTouchStart, { passive: false });
    return () => window.removeEventListener("touchstart", onTouchStart);
  }, []);

  // Keyboard nav — re-bound on isDesktop change so closures see fresh state.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLElement &&
        /input|textarea/i.test(e.target.tagName)
      ) {
        return;
      }
      if (e.key === "ArrowRight" || e.key === " ") {
        e.preventDefault();
        flipBookRef.current?.flipNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        flipBookRef.current?.flipPrev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDesktop]);

  return (
    <div className="tp-app tp-wide tp-app-edge">
      <div className="tp-topbar">
        <button
          className="tp-topbtn"
          onClick={() => navigate(backTo)}
          aria-label={t("common.back")}
        >
          <BackIcon />
        </button>
        {shareable ? (
          <button
            className="tp-topbtn tp-topbtn-wide"
            onClick={() =>
              openDialog({
                url: shareUrlFor(book.shortId),
                title: book.title || undefined,
              })
            }
          >
            <ShareIcon />
            <span>{t("reader.shareBook")}</span>
          </button>
        ) : (
          <div style={{ width: 38 }} />
        )}
        {ownerControls && (
          <ViewCountBadge views={book.viewCount} readers={book.uniqueReaderCount} />
        )}
        {ownerControls && <EditBookButton book={book} onRefresh={onRefresh} />}
      </div>
      <ShareToast status={shareStatus} error={shareError} />
      <ShareDialog
        open={shareDialog !== null}
        url={shareDialog?.url ?? ""}
        title={shareDialog?.title}
        onClose={onShareClose}
        imageEnabled={true}
        imageState={imageState}
        prepareImage={prepareImage}
        deliverImage={deliverImage}
        openImageInNewTab={openImageInNewTab}
        resetImage={resetImage}
        shortId={book.shortId}
        shareLinkDirect={shareLinkDirect}
      />
      <FixPageSheet
        open={ownerControls && fixTarget !== null}
        pageNumber={fixTarget?.position ?? 0}
        sourceKind={fixSourceKind}
        instruction={fixInstruction}
        busy={fixBusy}
        error={fixError}
        onSourceKind={setFixSourceKind}
        onInstruction={setFixInstruction}
        onSubmit={() => void submitFixPage()}
        onClose={closeFixPage}
      />

      <div className="tp-screen tp-no-pad" style={{ padding: "18px 0 36px" }}>
        <BookView
          ref={flipBookRef}
          book={book}
          pages={pages}
          ownerControls={ownerControls}
          onFixPage={openFixPage}
          onFlip={() => noteInteraction()}
        />
      </div>
    </div>
  );
}

function FixPageSheet({
  open,
  pageNumber,
  sourceKind,
  instruction,
  busy,
  error,
  onSourceKind,
  onInstruction,
  onSubmit,
  onClose,
}: {
  open: boolean;
  pageNumber: number;
  sourceKind: PageGenerationSourceKind;
  instruction: string;
  busy: boolean;
  error: string | null;
  onSourceKind: (kind: PageGenerationSourceKind) => void;
  onInstruction: (value: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  if (!open) return null;
  const choices: Array<{ kind: PageGenerationSourceKind; label: string; hint: string }> = [
    { kind: "auto", label: t("reader.fixChoiceAutoLabel"), hint: t("reader.fixChoiceAutoHint") },
    { kind: "photo", label: t("reader.fixChoicePhotoLabel"), hint: t("reader.fixChoicePhotoHint") },
    { kind: "drawing", label: t("reader.fixChoiceDrawingLabel"), hint: t("reader.fixChoiceDrawingHint") },
  ];
  return (
    <div className="tp-sheet-overlay" role="presentation" onClick={onClose}>
      <div
        className="tp-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tp-fix-page-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div id="tp-fix-page-title" className="tp-h2" style={{ marginBottom: 6 }}>
          {t("reader.fixPageTitle", { number: pageNumber })}
        </div>
        <div className="tp-body tp-soft" style={{ fontSize: 14, marginBottom: 16 }}>
          {t("reader.fixPageBody")}
        </div>

        <div className="tp-regen-choices" style={{ marginBottom: 14 }}>
          {choices.map((choice) => (
            <button
              key={choice.kind}
              type="button"
              className="tp-regen-choice"
              data-on={sourceKind === choice.kind ? "1" : "0"}
              disabled={busy}
              onClick={() => onSourceKind(choice.kind)}
            >
              <span>{choice.label}</span>
              <small>{choice.hint}</small>
            </button>
          ))}
        </div>

        <label className="tp-eyebrow" style={{ display: "block", marginBottom: 8 }}>
          {t("reader.optionalNote")}
        </label>
        <textarea
          className="tp-page-desc tp-regen-note"
          value={instruction}
          disabled={busy}
          maxLength={500}
          rows={3}
          placeholder={t("reader.notePlaceholder")}
          onChange={(e) => onInstruction(e.target.value)}
        />
        {error && <div className="tp-error">{error}</div>}
        <div className="tp-row" style={{ gap: 10, marginTop: 16 }}>
          <button className="tp-btn tp-btn-ghost" disabled={busy} onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button className="tp-btn tp-btn-primary" disabled={busy} onClick={onSubmit}>
            {busy ? t("reader.starting") : t("reader.startRegen")}
          </button>
        </div>
      </div>
    </div>
  );
}
